import { useEffect, useState } from 'react';
import { Menu, MenuItem, Sidebar } from 'react-pro-sidebar';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../Contexts/AuthContext';
import './Sidebar.css';

// Light Theme Icons
import lightAgentsIcon from '../assets/light/agents-icon.svg';
import lightChatIcon from '../assets/light/chat-icon.svg';
import lightChannelsIcon from '../assets/light/circle.svg';
import lightDataIntIcon from '../assets/light/data-int-icon.svg';
import lightDataStorageIcon from '../assets/light/data-storage-icon.svg';
import lightExamplesIcon from '../assets/light/examples.svg';
import lightHomeIcon from '../assets/light/home-icon.svg';
import lightMetricsIcon from '../assets/light/metrics-icon.svg';
import lightNotifyIcon from '../assets/light/notify.svg';
import lightOrganizationIcon from '../assets/light/organization-icon.svg';
import lightProductsIcon from '../assets/light/products.svg';
import lightPromptsIcon from '../assets/light/prompts-icon.svg';
import lightSettingsIcon from '../assets/light/settings-icon.svg';
import {
  default as lightUserHubIcon,
  default as lightUserIcon,
} from '../assets/light/user-hub-icon.svg';

// Dark Theme Icons
import darkAgentsIcon from '../assets/dark/agents-icon.svg';
import darkChatIcon from '../assets/dark/chat-icon.svg';
import darkChannelsIcon from '../assets/dark/circle.svg';
import darkDataIntIcon from '../assets/dark/data-int-icon.svg';
import darkDataStorageIcon from '../assets/dark/data-storage-icon.svg';
import darkExamplesIcon from '../assets/dark/examples.svg';
import darkHomeIcon from '../assets/dark/home-icon.svg';
import darkMetricsIcon from '../assets/dark/metrics-icon.svg';
import darkNotifyIcon from '../assets/dark/notify.svg';
import darkOrganizationIcon from '../assets/dark/organization-icon.svg';
import darkProductsIcon from '../assets/dark/products.svg';
import darkPromptsIcon from '../assets/dark/prompts-icon.svg';
import darkSettingsIcon from '../assets/dark/settings-icon.svg';
import {
  default as darkUserHubIcon,
  default as darkUserIcon,
} from '../assets/dark/user-hub-icon.svg';

function CustomSidebar() {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [theme, setTheme] = useState<string>(localStorage.getItem('theme') || 'light');
  const location = useLocation();
  const { organizationId } = useParams();

  // Get user from auth context
  const { status } = useAuth();
  // Access user permissions safely
  const permissions = status.permissions || [];

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.documentElement.getAttribute('data-theme') || 'light');
    });
    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const toggleSidebar = (): void => {
    setCollapsed(!collapsed);
  };

  // Choose Icons Based on Theme
  const icons =
    theme === 'light'
      ? {
          home: lightHomeIcon,
          organization: lightOrganizationIcon,
          user: lightUserIcon,
          prompts: lightPromptsIcon,
          agents: lightAgentsIcon,
          agentFrames: lightAgentsIcon,
          dataInt: lightDataIntIcon,
          dataStorage: lightDataStorageIcon,
          chat: lightChatIcon,
          metrics: lightMetricsIcon,
          userHub: lightUserHubIcon,
          settings: lightSettingsIcon,
          examples: lightExamplesIcon,
          channels: lightChannelsIcon,
          notifications: lightNotifyIcon,
          products: lightProductsIcon,
          artifacts: lightAgentsIcon,
          fragment: lightProductsIcon,
          recordRoute: lightChannelsIcon,
          connectionObjectMap: lightExamplesIcon,
          connectionObjects: lightDataIntIcon,
        }
      : {
          home: darkHomeIcon,
          organization: darkOrganizationIcon,
          user: darkUserIcon,
          prompts: darkPromptsIcon,
          agents: darkAgentsIcon,
          agentFrames: darkAgentsIcon,
          dataInt: darkDataIntIcon,
          dataStorage: darkDataStorageIcon,
          chat: darkChatIcon,
          metrics: darkMetricsIcon,
          userHub: darkUserHubIcon,
          settings: darkSettingsIcon,
          examples: darkExamplesIcon,
          channels: darkChannelsIcon,
          notifications: darkNotifyIcon,
          products: darkProductsIcon,
          artifacts: darkAgentsIcon,
          fragment: darkProductsIcon,
          recordRoute: darkChannelsIcon,
          connectionObjectMap: darkExamplesIcon,
          connectionObjects: darkDataIntIcon,
        };

  // Route Logic
  const isOnOrganizationsRoot =
    location.pathname === '/' ||
    location.pathname === '/components-list' ||
    location.pathname === '/super-admin' ||
    location.pathname === '/organizations';
  const isOnOrganizationPage = organizationId !== undefined;

  const isActiveRoute = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const informationPackagePath = `/organizations/${organizationId}/information-package`;
  const informationPackageCollectionsPath = `/organizations/${organizationId}/information-package-collections`;
  const auditOperationsPath = `/organizations/${organizationId}/audit-operations`;

  return (
    <div
      className={`sidebar-container ${
        theme === 'light' ? 'sidebar-light' : 'sidebar-dark'
      } ${collapsed ? 'collapsed' : ''}`}
    >
      {/* Toggle Button */}
      <button
        className={`toggle-btn ${collapsed ? 'collapsed' : 'expanded'}`}
        onClick={toggleSidebar}
      ></button>

      <div className="logo-wrapper">
        <Link className="logo-link" to="/organizations">
          <div className="logo-title">SURE</div>
        </Link>
      </div>

      {/* Sidebar */}
      <Sidebar collapsed={collapsed} className="custom-sidebar">
        <Menu>
          <div className="separator-label">
            <p className="sidebar-label">General</p>
          </div>
          {/* Home - Always Visible */}
          {permissions.includes('all:organizations') && (
            <MenuItem
              icon={<img src={icons.home} alt="Home" className="menu-icon" />}
              component={<Link to="/organizations" />}
              active={location.pathname === '/organizations'}
            >
              Organizations
            </MenuItem>
          )}

          {permissions.includes('all:organizations') && (
            <MenuItem
              icon={<img src={icons.examples} alt="components list" className="menu-icon" />}
              component={<Link to={`/components-list`} />}
              active={location.pathname === '/components-list'}
            >
              Components
            </MenuItem>
          )}

          {/* Other Menu Items - Only if NOT on Organizations Root */}
          {!isOnOrganizationsRoot && (
            <>
              {/* Organization */}
              {(permissions.includes('all:organization') ||
                permissions.includes('read:organization')) && (
                <MenuItem
                  icon={<img src={icons.organization} alt="Organization" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}`} />}
                  active={location.pathname === `/organizations/${organizationId}`}
                >
                  Organization
                </MenuItem>
              )}

              {/* Prompts Hub */}

              <div className="separator-label">
                <p className="sidebar-label">Logic</p>
              </div>
              {permissions.includes('all:assistant') && (
                <MenuItem
                  icon={<img src={icons.prompts} alt="Prompts Hub" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}/prompts-hub`} />}
                  active={location.pathname.includes('/prompts-hub')}
                >
                  Prompts Hub
                </MenuItem>
              )}

              {/* Users Hub */}
              {permissions.includes('all:organization') && (
                <MenuItem
                  icon={<img src={icons.user} alt="Users Hub" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}/users-hub`} />}
                  active={location.pathname.includes('/users')}
                >
                  Users Hub
                </MenuItem>
              )}

              {/* Agents Hub */}
              {permissions.includes('all:assistant') && (
                <MenuItem
                  icon={<img src={icons.agents} alt="Agents Hub" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}/agents-hub`} />}
                  active={location.pathname.includes('/agents-hub')}
                >
                  Agents Hub
                </MenuItem>
              )}
              {permissions.includes('all:assistant') && (
                <MenuItem
                  icon={<img src={icons.agents} alt="Agents Frames" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}/agent-frames`} />}
                  active={location.pathname.includes('/agent-frames')}
                >
                  Agent Frames
                </MenuItem>
              )}
              {/* Simulation Dataset */}
              {permissions.includes('all:assistant') && (
                <MenuItem
                  icon={<img src={icons.agents} alt="Simulation Dataset" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}/simulation-datasets`} />}
                  active={location.pathname.includes('/simulation-datasets')}
                >
                  Simulation Dataset
                </MenuItem>
              )}
              {permissions.includes('all:assistant') && (
                <MenuItem
                  icon={<img src={icons.agents} alt="Task Schedule" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}/task-schedule`} />}
                  active={location.pathname.includes('/task-schedule')}
                >
                  Task Schedule
                </MenuItem>
              )}
              {permissions.includes('all:assistant') && (
                <MenuItem
                  icon={<img src={icons.agents} alt="Markers" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}/markers`} />}
                  active={location.pathname.includes('/markers')}
                >
                  Markers
                </MenuItem>
              )}

              {permissions.includes('all:document') && (
                <>
                  <MenuItem
                    icon={<img src={icons.dataInt} alt="Documents Hub" className="menu-icon" />}
                    component={<Link to={`/organizations/${organizationId}/documents-hub`} />}
                    active={location.pathname.includes('/documents-hub')}
                  >
                    Documents Hub
                  </MenuItem>
                  <MenuItem
                    icon={
                      <img
                        src={icons.products}
                        alt="Documents collection Hub"
                        className="menu-icon"
                      />
                    }
                    component={
                      <Link to={`/organizations/${organizationId}/document-collections`} />
                    }
                    active={location.pathname.includes('/document-collections')}
                  >
                    Collections Hub
                  </MenuItem>
                  <MenuItem
                    icon={
                      <img src={icons.dataInt} alt="Information Package" className="menu-icon" />
                    }
                    component={<Link to={informationPackagePath} />}
                    active={isActiveRoute(informationPackagePath)}
                  >
                    Information Package
                  </MenuItem>

                  <MenuItem
                    icon={
                      <img
                        src={icons.products}
                        alt="Information Package Collections"
                        className="menu-icon"
                      />
                    }
                    component={<Link to={informationPackageCollectionsPath} />}
                    active={isActiveRoute(informationPackageCollectionsPath)}
                  >
                    IP Collections
                  </MenuItem>
                  <MenuItem
                    icon={<img src={icons.products} alt="Catalogs" className="menu-icon" />}
                    component={<Link to={`/organizations/${organizationId}/product-catalogs`} />}
                    active={location.pathname.includes('/product-catalogs')}
                  >
                    Products Hub
                  </MenuItem>
                </>
              )}

              {/* Channels */}
              {permissions.includes('all:channel') && (
                <MenuItem
                  icon={<img src={icons.channels} alt="Chats" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}/channels`} />}
                  active={location.pathname.includes('/channels')}
                >
                  Channels Hub
                </MenuItem>
              )}

              {/* Chats */}
              {permissions.includes('all:channel_message') && (
                <MenuItem
                  icon={<img src={icons.chat} alt="Chats" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}/chats`} />}
                  active={location.pathname.includes('/chats')}
                >
                  Chats Hub
                </MenuItem>
              )}

              {permissions.includes('all:assistant') && (
                <MenuItem
                  icon={
                    <img src={icons.notifications} alt="Notifications Hub" className="menu-icon" />
                  }
                  component={<Link to={`/organizations/${organizationId}/notifications-hub`} />}
                  active={location.pathname.includes('/notifications-hub')}
                >
                  {' '}
                  Notifications Hub
                </MenuItem>
              )}

              {permissions.includes('all:assistant') && (
                <MenuItem
                  icon={<img src={icons.examples} alt="Dictionary Hub" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}/taxonomy-dictionary`} />}
                  active={location.pathname.includes('/taxonomy-dictionary')}
                >
                  {' '}
                  Dictionary Hub
                </MenuItem>
              )}

              {permissions.includes('all:assistant') && (
                <MenuItem
                  icon={<img src={icons.dataInt} alt="Thematic" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}/thematic`} />}
                  active={location.pathname.includes('/thematic')}
                >
                  {' '}
                  Thematic
                </MenuItem>
              )}

              {permissions.includes('all:organization') && (
                <MenuItem
                  icon={<img src={icons.metrics} alt="API Key" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}/api-key`} />}
                  active={location.pathname.includes('/api-key')}
                >
                  {' '}
                  API Key Hub
                </MenuItem>
              )}

              {permissions.includes('all:organization') && (
                <MenuItem
                  icon={<img src={icons.examples} alt="Audit-operation" className="menu-icon" />}
                  component={<Link to={auditOperationsPath} />}
                  active={isActiveRoute(auditOperationsPath)}
                >
                  {' '}
                  Audit Operation
                </MenuItem>
              )}

              {permissions.includes('all:organization') && (
                <>
                  <MenuItem
                    icon={<img src={icons.artifacts} alt="Artifacts" className="menu-icon" />}
                    component={<Link to={`/organizations/${organizationId}/artifacts`} />}
                    active={location.pathname.includes('/artifacts')}
                  >
                    {' '}
                    Artifact Hub
                  </MenuItem>
                  <MenuItem
                    icon={<img src={icons.channels} alt="Connections" className="menu-icon" />}
                    component={<Link to={`/organizations/${organizationId}/connections`} />}
                    active={location.pathname.includes('/connections')}
                  >
                    {' '}
                    Connections
                  </MenuItem>
                  <MenuItem
                    icon={<img src={icons.fragment} alt="Fragments" className="menu-icon" />}
                    component={<Link to={`/organizations/${organizationId}/fragments`} />}
                    active={location.pathname.includes('/fragments')}
                  >
                    {' '}
                    Fragments
                  </MenuItem>
                  {/* <MenuItem
                                icon={<img src={icons.connectionObjectMap} alt="ConnectionsObjectMap"
                                           className="menu-icon"/>}
                                component={<Link to={`/organizations/${organizationId}/connection-object-map`}/>}
                                active={location.pathname.includes("/connection-object-map")}
                              > Connections Object Map
                              </MenuItem> */}

                  <MenuItem
                    icon={
                      <img
                        src={icons.connectionObjects}
                        alt="ConnectionObjects"
                        className="menu-icon"
                      />
                    }
                    component={<Link to={`/organizations/${organizationId}/connection-objects`} />}
                    active={location.pathname.includes('/connection-objects')}
                  >
                    {' '}
                    Connection Objects
                  </MenuItem>

                  <MenuItem
                    icon={<img src={icons.recordRoute} alt="Record route" className="menu-icon" />}
                    component={<Link to={`/organizations/${organizationId}/record-route`} />}
                    active={location.pathname.includes('/record-route')}
                  >
                    {' '}
                    Record Route Hub
                  </MenuItem>

                  <MenuItem
                    icon={
                      <img src={icons.metrics} alt="Post-Call Analysis" className="menu-icon" />
                    }
                    component={<Link to={`/organizations/${organizationId}/post-call-analysis`} />}
                    active={location.pathname.includes('/post-call-analysis')}
                  >
                    {' '}
                    Post-Call Analysis
                  </MenuItem>
                </>
              )}
              <div className="separator-label">
                <p className="sidebar-label">Settings</p>
              </div>

              {permissions.includes('all:organization') && (
                <MenuItem
                  icon={<img src={icons.examples} alt="Dictionary Hub" className="menu-icon" />}
                  component={<Link to={`/organizations/${organizationId}/org-settings`} />}
                  active={location.pathname.includes('/org-settings')}
                >
                  Settings
                </MenuItem>
              )}
            </>
          )}
        </Menu>
      </Sidebar>
    </div>
  );
}

export default CustomSidebar;
